import React from 'react';
import { Calendar, User, Clock, BookOpen, ChevronRight } from '../lib/icons';
import { BlogPost } from '../lib/supabase';
import { calculateReadingTime } from '../utils/readingTimeUtils';
import { getBlogImage, BLOG_IMAGE_DEFAULT } from '../utils/imageUtils';

interface BlogCardProps {
  post: BlogPost;
  onClick: (postId: string) => void;
  className?: string;
  imageHeight?: 'default' | 'large';
}

const BlogCard: React.FC<BlogCardProps> = ({ post, onClick, className = "", imageHeight = "default" }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <article
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer ${className}`}
      onClick={() => onClick(post.id)}
    >
      <div className="aspect-w-16 aspect-h-9">
        <img
          src={getBlogImage(post.image_url)}
          alt={post.title}
          loading="lazy"
          decoding="async"
          width={BLOG_IMAGE_DEFAULT.width}
          height={BLOG_IMAGE_DEFAULT.height}
          onError={(e) => { e.currentTarget.src = getBlogImage(undefined); }}
          className={`w-full object-cover rounded-t-lg transition-transform duration-300 hover:scale-105 ${
            imageHeight === 'large' ? 'h-48 sm:h-64' : 'h-32 sm:h-40'
          }`}
        />
      </div>
      <div className="p-4">
        {/* Titel als prominentestes Element */}
        <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight">
          {post.title}
        </h3>
        
        {/* Excerpt */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
          {post.excerpt}
        </p>
        
        {/* Meta-Informationen kompakt */}
        <div className="flex items-center text-xs text-gray-500 mb-4">
          <Calendar className="w-3 h-3 mr-1" />
          <span>{formatDate(post.published_at || post.created_at)}</span>
          <span className="mx-2">•</span>
          <User className="w-3 h-3 mr-1" />
          <span>{post.author}</span>
          <span className="mx-2">•</span>
          <Clock className="w-3 h-3 mr-1" />
          <span>{calculateReadingTime(post.content)}</span>
        </div>
        
        {/* Kategorie und Typ-Badge */}
        <div className="flex items-center space-x-2 mb-3">
          {/* Kategorie als prominenter Badge */}
          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
            {post.category}
          </span>
          {/* Typ-Badge kleiner und dezenter */}
          <span className={`px-2 py-1 text-xs rounded-full ${
            post.post_type === 'news'
              ? 'bg-blue-100 text-blue-800'
              : post.post_type === 'fachbeitrag'
              ? 'bg-purple-100 text-purple-800'
              : 'bg-green-100 text-green-800'
          }`}>
            {post.post_type === 'artikel' ? 'Artikel' :
             post.post_type === 'news' ? 'News' : 'Fachbeitrag'}
          </span>
        </div>

        {/* Lesen-Button rechtsbündig */}
        <div className="flex justify-end">
          <button
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onClick(post.id);
            }}
            aria-label={`Artikel ${post.title} lesen`}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            Lesen
            <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;